import * as React from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PhotoPicker from '../components/PhotoPicker';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MAX_COMMENT_LENGTH, formatUKTime, getUKTimeOffset, reportClockTime } from '../lib/clockReports';
import { sentenceCase } from '../lib/text';

export default function ReportClockTimeDialog(props) {
    const { open, onClose, place } = props;
    const [offset, setOffset] = React.useState(0);
    const [ukTime, setUkTime] = React.useState('');
    const [seenTime, setSeenTime] = React.useState('');
    const [comment, setComment] = React.useState('');
    const [photos, setPhotos] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [result, setResult] = React.useState(null);

    // Reset and sync with the server's UK time each time the dialog opens
    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setSeenTime('');
        setComment('');
        setPhotos([]);
        setResult(null);
        setOffset(0);
        getUKTimeOffset().then((serverOffset) => {
            if (!cancelled && serverOffset !== null) setOffset(serverOffset);
        });
        return () => { cancelled = true; };
    }, [open]);

    // Tick the displayed UK time every second
    React.useEffect(() => {
        if (!open) return;
        const update = () => setUkTime(formatUKTime(Date.now() + offset));
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [open, offset]);

    const handleSubmit = async () => {
        setSubmitting(true);
        const response = await reportClockTime(place.place_id, seenTime, comment, photos);
        setSubmitting(false);
        if (response.ok) {
            setResult({ severity: 'success', message: 'Thank you! Your report has been recorded.' });
        } else {
            setResult({ severity: 'error', message: response.message });
        }
    };

    const done = result?.severity === 'success';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Report clock time</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {place ? sentenceCase(place.title) : ''}
                </DialogContentText>
                <Typography variant="overline" display="block" sx={{ mt: 2 }}>
                    Current time in the UK
                </Typography>
                <Typography variant="h4" component="p" aria-live="off" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {ukTime}
                </Typography>
                <TextField
                    id="clock-seen-time"
                    label="Time you see on the clock"
                    type="time"
                    value={seenTime}
                    onChange={(event) => setSeenTime(event.target.value)}
                    disabled={done}
                    fullWidth
                    sx={{ mt: 3 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    helperText="24-hour time, e.g. 14:05"
                />
                <TextField
                    id="clock-comment"
                    label="Comments (optional)"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    disabled={done}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    sx={{ mt: 2 }}
                    inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                    helperText={`Anything else we should know, e.g. the clock has stopped. ${comment.length}/${MAX_COMMENT_LENGTH}`}
                />
                <PhotoPicker photos={photos} onChange={setPhotos} disabled={done || submitting} />
                {result ? <Alert severity={result.severity} sx={{ mt: 2 }}>{result.message}</Alert> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
                {done ? null : (
                    <Button onClick={handleSubmit} disabled={!seenTime || submitting}>
                        Submit
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
